import { createContext, useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';  // Named import for jwt-decode

// project imports
import { login as apiLogin, logout as apiLogout, getProfile } from '../utils/api';  // API helpers

// context & initial state
const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

// JWTContext
const JWTContext = createContext({  // FIXED: Rename to JWTContext (matches named import in useAuth.js)
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

// ==============================|| JWT CONTEXT & DISPATCH ||============================== //

export const JWTProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);  // For UI loading
  const [error, setError] = useState(null);  // For auth errors
  const isAuthInitialized = useRef(false);  // Prevent re-init

  // FIXED: Direct localStorage helpers (no JSON.parse on token; safe parse on user)
  const getStoredToken = () => localStorage.getItem('accessToken');
  const getStoredUser = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch (parseErr) {
      console.warn('Corrupted user data in localStorage—clearing:', parseErr);
      localStorage.removeItem('user');
      return null;
    }
  };
  const setStoredToken = (token) => localStorage.setItem('accessToken', token);  // String—no parse
  const setStoredUser = (user) => localStorage.setItem('user', JSON.stringify(user));
  const removeStoredToken = () => localStorage.removeItem('accessToken');
  const removeStoredUser = () => localStorage.removeItem('user');

  // Check auth on mount (restore from localStorage/JWT)
  useEffect(() => {
    if (isAuthInitialized.current) return;
    isAuthInitialized.current = true;

    const initAuth = async () => {
      try {
        const storedToken = getStoredToken();
        const storedUser = getStoredUser();
        if (storedToken && storedUser) {
          // Verify token not expired
          const decoded = jwtDecode(storedToken);
          if (decoded.exp * 1000 > Date.now()) {
            setState({
              isAuthenticated: true,
              isInitialized: true,
              user: storedUser  // From login response
            });
            console.log('JWT Init: Token valid—logged in as', storedUser.email);  // Debug
          } else {
            console.log('JWT Init: Token expired—logging out');
            await logout();  // Clear expired
          }
        } else {
          console.log('JWT Init: No token—guest mode');  // Your log
          setState((prev) => ({ ...prev, isInitialized: true }));
        }
      } catch (err) {
        console.error('Auth init error:', err);
        setState((prev) => ({ ...prev, isInitialized: true }));
      }
    };
    initAuth();
  }, []);  // Runs once on mount

  // Override login: Call API, store JWT/user, update state
  const login = async (credentials) => {  // FIXED: Accept object {email, password} (matches useAuth/apiLogin)
    const { email, password } = credentials;  // Destructure for validation/logging
    if (!email || !password) {
      throw new Error('Email and password required');
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await apiLogin(credentials);  // Pass full object—no destructuring needed
      if (response.status === 'success') {
        // FIXED: Use direct localStorage (string for token, JSON for user)
        setStoredToken(response.accessToken);
        setStoredUser(response.user);
        setState({
          isAuthenticated: true,
          isInitialized: true,
          user: response.user  // Includes id, email, role, etc.
        });
        console.log('Login success:', response.user.email);  // Your log
        return { success: true };
      }
      throw new Error(response.message || 'Login failed');
    } catch (err) {
      console.error('Login error:', err);  // FIXED: Log full error
      setError(err.message || 'Invalid credentials');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };
  // Override logout: Clear API storage, state
  const logout = async () => {
    try {
      await apiLogout();  // Clears localStorage (if it does; redundant with direct calls)
      removeStoredToken();
      removeStoredUser();
      setState({
        isAuthenticated: false,
        isInitialized: true,
        user: null
      });
      console.log('Logout complete');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Register: Stub (implement as needed)
  const register = async (email, password, first_name, last_name) => {
    throw new Error('Register not implemented—use admin panel');
  };

  // Refresh profile if needed
  const refreshProfile = async () => {
    try {
      const response = await getProfile();
      if (response.status === 'success') {
        setStoredUser(response.user);  // Update storage
        setState((prev) => ({ ...prev, user: response.user }));
      }
    } catch (err) {
      console.error('Profile refresh error:', err);
      logout();  // If 401, force logout
    }
  };

  return (
    <JWTContext.Provider  // FIXED: Use JWTContext (matches import)
      value={{
        ...state,
        isLoggedIn: state.isAuthenticated,  // Berry template compatibility for GuestGuard, Login, etc.
        method: 'jwt',
        isLoading,  // Expose for UI
        error,      // Expose for error display
        login,
        logout,
        register,
        refreshProfile
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;  // FIXED: Default export as JWTContext (matches named import in useAuth.js)